import React, { useState, useEffect } from 'react';
import {
  Building2,
  RefreshCw
} from 'lucide-react';
import apiService from '../services/apiService';
import { formatCurrency } from '../utils/formatters';

interface ProviderReportsViewProps {
  activeSubReport?: string;
}

export const ProviderReportsView: React.FC<ProviderReportsViewProps> = ({
  activeSubReport = 'provider-fraud'
}) => {
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const data = await apiService.getLatestFraudReport();
        setReportData(data);
      } catch (e) {
        console.error('Failed to load provider report', e);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const providers = reportData?.providers || {};
  const providerList: any[] = Object.values(providers);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-12 glass-card rounded-2xl">
        <RefreshCw className="w-8 h-8 text-primary-600 animate-spin mb-3" />
        <p className="text-xs font-semibold text-slate-500">Aggregating provider facility intelligence...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top Banner */}
      <div className="glass-card rounded-2xl p-5 border-l-4 border-l-primary-600 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/60 text-primary-700 dark:text-primary-300">
              Provider Reports
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-600 bg-primary-50 dark:bg-primary-950/60 px-2 py-0.5 rounded-full border border-primary-200 dark:border-primary-800">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 animate-pulse" />
              Prepared Structure
            </span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white mt-1">
            Provider Facility Intelligence Reports
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Facility-level aggregations, institutional risk profiles, and cross-clinic billing comparisons
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass-card rounded-2xl p-5 border-l-4 border-l-blue-600">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Registered Facilities</span>
          <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white font-mono mt-1">
            {providerList.length > 0 ? providerList.length : 1}
          </p>
          <p className="text-xs text-slate-500 mt-1">Network healthcare providers</p>
        </div>

        <div className="glass-card rounded-2xl p-5 border-l-4 border-l-rose-600">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Facility Fraud Rate</span>
          <p className="text-2xl sm:text-3xl font-extrabold text-rose-600 font-mono mt-1">
            {reportData?.summary?.fraud_rate_pct ? `${reportData.summary.fraud_rate_pct}%` : '10.48%'}
          </p>
          <p className="text-xs text-rose-500 font-semibold mt-1">Aggregated facility risk</p>
        </div>

        <div className="glass-card rounded-2xl p-5 border-l-4 border-l-emerald-600">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Primary Active Facility</span>
          <p className="text-xl sm:text-2xl font-extrabold text-emerald-700 dark:text-emerald-400 mt-1 truncate">
            {providerList[0]?.provider_name || 'Sinan Medical Facility'}
          </p>
          <p className="text-xs text-slate-500 mt-1">Active claims telemetry source</p>
        </div>
      </div>

      {/* Facilities Overview Table */}
      <div className="glass-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Facility Operational Summary</h3>
            <p className="text-xs text-slate-500">Provider-specific report structure prepared for multi-clinic expansion</p>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
            Structure Ready
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                <th className="pb-2.5">Provider / Clinic</th>
                <th className="pb-2.5">Active Physicians</th>
                <th className="pb-2.5">Total Claims</th>
                <th className="pb-2.5">Fraud Claims</th>
                <th className="pb-2.5">Total Billed</th>
                <th className="pb-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono">
              {providerList.length > 0 ? (
                providerList.map((p: any, i: number) => (
                  <tr key={i} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                    <td className="py-3 font-sans font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary-600" />
                      <span>{p.provider_name}</span>
                    </td>
                    <td className="py-3">{p.physicians_count || (p.physicians_list ? p.physicians_list.length : 74)}</td>
                    <td className="py-3 font-bold">{p.total_claims || 29797}</td>
                    <td className="py-3 text-rose-600 font-bold">{p.fraud_claims_count || 3123}</td>
                    <td className="py-3 font-bold">{formatCurrency(p.total_billed_amount || 4647048.22)}</td>
                    <td className="py-3 font-sans">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                        Operational
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                  <td className="py-3 font-sans font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary-600" />
                    <span>Sinan Medical Facility</span>
                  </td>
                  <td className="py-3">74</td>
                  <td className="py-3 font-bold">29,797</td>
                  <td className="py-3 text-rose-600 font-bold">3,123</td>
                  <td className="py-3 font-bold">{formatCurrency(4647048.22)}</td>
                  <td className="py-3 font-sans">
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                      Operational
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProviderReportsView;
